import { useCallback, useMemo } from 'react'
import * as React from 'react'

import Attachments from './common/Attachments'
import CornerControls from './common/CornerControls'
import { DetailCardAndPinLayout, TransportCardLayout } from './common/DetailCardAndPinLayout'
import ItemTitle from './common/ItemTitle'
import type { DetailItemCardVariant } from './common/types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useToggle from 'hooks/useToggle'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import type { CarRentalItem } from 'tripPlanner/types/tripItem'
import { formatShortDateRange } from 'tripPlanner/utils'
import { isSamePlace } from 'tripPlanner/utils/places'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import GroupWithSeparator from '../CleanCard/common/GroupWithSeparator'
import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import TransportDefaultLogo from './common/TransportDefaultLogo'

interface Props {
  item: CarRentalItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailCarRentalCard({ item, variant }: Props) {
  const [inFocus, , setInFocusOn, setInFocusOff] = useToggle(false)
  const [showComment, , setShowCommentOn, setShowCommentOff] = useToggle(!!item.notes)

  const { data: trip } = useTrip({ tripId: item.tripId })

  const { startPlace, endPlace } = item

  const isMultiDay = item.endDate && item.endDate.diff(item.startDate, 'days') > 0

  let title: string
  if (isMultiDay && variant === 'itinerary' || !endPlace) {
    title = `Pick up at ${startPlace.name}`
  } else if (isSamePlace(startPlace, endPlace)) {
    title = `Pick up and drop off at ${startPlace.name}`
  } else {
    title = `Pick up at ${startPlace.name}, drop off at ${endPlace.name}`
  }

  const timeDescription = formatShortDateRange({ ...item, includeYear: false })

  const { itemNumber, itemColor } = useMemo(
    () => getItemPinBadge(item, variant, trip),
    [item, variant, trip],
  )

  const onRequestAddComment = useCallback(() => {
    setShowCommentOn()
    setInFocusOn()
  }, [setShowCommentOn, setInFocusOn])

  const onNotesChange = useCallback((notes: string | undefined) => {
    if (notes) {
      setShowCommentOn()
    } else {
      setShowCommentOff()
    }
  }, [setShowCommentOn, setShowCommentOff])

  return (
    <DetailCardAndPinLayout data-testid="detail-card-car-rental">
      <DetailCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </DetailCardAndPinLayout.Pin>
      <TransportCardLayout>
        <TransportCardLayout.Image>
          <HoverOnlyRegenerateItemButton item={item} />
          <TransportDefaultLogo type="CAR_RENTAL" />
        </TransportCardLayout.Image>
        <TransportCardLayout.Content>
          <CornerControls item={item} onRequestAddComment={onRequestAddComment} />
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              <ItemTitle>{title}</ItemTitle>
              <GroupWithSeparator direction="horizontal" verticalAlign="center" gap={4} desktopGap={8} wrap="wrap">
                {item.companyName && (
                  <BodyText variant="small" colour="neutral-two">
                    {item.companyName}
                  </BodyText>
                )}
                {timeDescription && (
                  <BodyTextBlock
                    variant="small"
                    colour="neutral-two"
                  >
                    {timeDescription}
                  </BodyTextBlock>
                )}
              </GroupWithSeparator>
            </VerticalSpacer>
            <Attachments item={item} />
          </VerticalSpacer>
        </TransportCardLayout.Content>
        {showComment && (
          <TransportCardLayout.Comment>
            <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              inFocus={inFocus}
              onFocus={setInFocusOn}
              onBlur={setInFocusOff}
              onNotesChange={onNotesChange}
            />
          </TransportCardLayout.Comment>
        )}
      </TransportCardLayout>
    </DetailCardAndPinLayout>
  )
}

export default DetailCarRentalCard
